import React, { useEffect, useState } from 'react';
import { BadgePercent, Baby, Gift, Tag } from 'lucide-react';
import { formatPrice } from './pricing';
import { PROMO_LABEL, useDealCode } from './dealCode';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const UNIT = { room_night: 'per room / night', person_night: 'per person / night', stay: 'per stay' };
const VA_NAMES = { breakfast: 'Breakfast', half_board: 'Half board', full_board: 'Full board', pets: 'Pets welcome', parking: 'Parking', airport_shuttle: 'Airport shuttle', park_sleep_fly: 'Park, sleep & fly', wifi: 'High-speed internet', fnb_credit: 'Food & drink credit', early_checkin: 'Early check-in', late_checkin: 'Late check-in', late_checkout: 'Late check-out', spa: 'Spa access', beach: 'Beach bed & umbrella', property_credit: 'Property services credit' };

const policy = (p) => (p.cancel_days == null ? 'Non-refundable' : p.cancel_days === 0 ? 'Free cancellation until arrival' : `Free cancellation until ${p.cancel_days} day${p.cancel_days > 1 ? 's' : ''} before arrival`);
const childText = (b) => (b.mode === 'free' ? 'free' : b.mode === 'full' ? 'adult rate' : b.mode === 'percent' ? `${b.value}% of adult rate` : `€${b.value}/night`);
const ages = (b) => (b.from_age === b.to_age ? `${b.from_age}` : `${b.from_age}–${b.to_age}`);

const Offer = ({ title, sub, total, perNight, base, cta, onBook, testId, tone }) => (
  <div className={`rounded-xl border p-3 flex items-center gap-3 ${tone || 'border-gray-200 bg-white'}`} data-testid={testId}>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-900 text-sm">{title}</p>
      <p className="text-xs text-gray-600">{sub}</p>
    </div>
    <div className="text-right">
      {base != null && base !== total && <p className="text-xs text-gray-400 line-through">{formatPrice(base)}</p>}
      <p className="font-bold text-gray-900">{formatPrice(total)}</p>
      <p className="text-[11px] text-gray-500">{formatPrice(perNight)}/night</p>
    </div>
    {onBook && <button type="button" onClick={onBook} className="text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-2" data-testid={`${testId}-book`}>{cta}</button>}
  </div>
);

// Website mirror of the admin "Rates & availability" settings: deals, rate plans, extras and child policy for this stay.
export const RateOffers = ({ pricing, bookingData, onBook }) => {
  const [offers, setOffers] = useState(null);
  const [applied, setApplied] = useDealCode();
  const [code, setCode] = useState(applied);
  const roomId = pricing?.beds24_room_id;
  const ci = bookingData?.checkIn;
  const co = bookingData?.checkOut;
  useEffect(() => {
    if (!ci || !co) return undefined;
    let alive = true;
    fetch(`${API_URL}/api/public/rate-offers?check_in=${ci}&check_out=${co}${roomId ? `&room_id=${roomId}` : ''}${applied ? `&code=${encodeURIComponent(applied)}` : ''}`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setOffers(d); }).catch(() => {});
    return () => { alive = false; };
  }, [ci, co, roomId, applied]);
  if (!offers || !pricing?.pricing?.total_price) return null;
  const total = pricing.pricing.total_price;
  const perNight = pricing.pricing.price_per_night;
  const off = (pct) => ({ total: Math.round(total * (1 - pct / 100)), perNight: Math.round(perNight * (1 - pct / 100)) });
  const promos = (offers.promotions || []).slice(0, 1); // Booking.com rule: one deal at a time — the best one
  const plans = offers.rate_plans || [];
  const adds = offers.value_adds || [];
  const kids = offers.child_rates || [];
  const secretMissing = applied && !promos.some((p) => p.voucher_code === applied);
  const promoRows = promos.map((p) => { const d = off(p.discount_pct); return <Offer key={p.id} title={`${PROMO_LABEL[p.type]} · −${p.discount_pct}%`} sub={`${p.name} · code ${p.voucher_code} applied at checkout`} total={d.total} perNight={d.perNight} base={total} cta="Book this deal" onBook={() => onBook(p.voucher_code)} testId={`offer-promo-${p.id}`} tone="border-emerald-300 bg-emerald-50" />; });
  const planRows = plans.map((p) => { const bookable = p.discount_pct > 0 && p.voucher_code; const d = off(bookable ? p.discount_pct : 0); return <Offer key={p.id} title={p.name} sub={`${policy(p)}${p.min_los > 1 ? ` · min ${p.min_los} nights` : ''}${bookable ? ` · ${p.discount_pct}% below Standard Rate` : ''}`} total={d.total} perNight={d.perNight} base={bookable ? total : null} cta="Book" onBook={() => onBook(bookable ? p.voucher_code : '')} testId={`offer-plan-${p.id}`} />; });
  const addRows = adds.map((a) => <li key={a.id} className="flex justify-between text-xs py-1 border-b last:border-b-0 border-gray-100" data-testid={`offer-va-${a.id}`}><span className="text-gray-700">{VA_NAMES[a.id] || a.id}</span><span className={a.free ? 'text-emerald-700 font-semibold' : 'text-gray-900'}>{a.free ? 'Included' : `€${a.price} ${UNIT[a.unit] || ''}`}</span></li>);
  const kidText = kids.map((b) => `${ages(b)} yrs ${childText(b)}`).join(' · ');
  return (
    <div className="mb-6 space-y-3" data-testid="rate-offers">
      {(promoRows.length > 0 || planRows.length > 0) && (
        <div>
          <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2"><Tag size={16} className="text-orange-500" /> Choose your rate</h3>
          <div className="space-y-2">
            <Offer title="Standard Rate" sub="Best flexible price for these dates" total={total} perNight={perNight} cta="Book" onBook={() => onBook('')} testId="offer-standard" />
            {planRows}
            {promoRows}
          </div>
        </div>
      )}
      <div className="rounded-xl border border-dashed border-gray-300 p-3" data-testid="offer-secret">
        <p className="text-xs text-gray-600 flex items-center gap-1 mb-1"><BadgePercent size={14} className="text-orange-500" /> Have a secret deal code?</p>
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE" className="border rounded-lg px-2 py-1.5 text-sm w-full font-mono" data-testid="offer-secret-input" />
          <button type="button" onClick={() => setApplied(code.trim())} className="text-xs font-semibold border border-orange-500 text-orange-600 rounded-lg px-3" data-testid="offer-secret-apply">Apply</button>
        </div>
        {secretMissing && <p className="text-[11px] text-red-600 mt-1" data-testid="offer-secret-invalid">This code is not valid for these dates.</p>}
      </div>
      {addRows.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 text-sm mb-1 flex items-center gap-2"><Gift size={16} className="text-orange-500" /> Extras</h3>
          <ul>{addRows}</ul>
        </div>
      )}
      {kids.length > 0 && <p className="text-[11px] text-gray-500 flex items-start gap-1" data-testid="offer-children"><Baby size={14} className="text-orange-500 shrink-0" /> Children: {kidText}</p>}
    </div>
  );
};
